import clsx from 'clsx';
import { Typography } from '../typography';
import { CaseStatusFilter, PhaseNameCase, StatusColorCase } from '@/interfaces';
import { Check, List, X } from 'lucide-react';
import { END_STATUSES_CASE, HIDE_PIN_STATUSES_CASE } from '@/const/cases';
import { TooltipComponent } from '../tooltip';
import Image from 'next/image';
import Icons from '@/const/icons';

interface Props {
	status: CaseStatusFilter;
	statusColor?: StatusColorCase;
	withLabel?: boolean;
	isDraft?: boolean;
	phaseName?: PhaseNameCase;
	showDot?: boolean;
}

export const LabelLawyerState: React.FC<Props> = ({
	status,
	statusColor,
	withLabel = true,
	phaseName,
	showDot = true,
}) => {
	const getText = () => {
		const commonStatus: CaseStatusFilter[] = [
			'Active Case',
			'Applicant Intake',
			'Settlement',
		];

		if (commonStatus.includes(status) && statusColor === 'red') {
			return 'Action required from lawyer.';
		}

		if (commonStatus.includes(status) && statusColor === 'green') {
			return 'No action for lawyer.';
		}

		if (status === 'Investigation') {
			return (
				<>
					<p>Case created but has</p>
					<p>not been published.</p>
				</>
			);
		}

		if (status === 'Case Unsuccessful') {
			return (
				<>
					<p>The outcome of the case was</p>
					<p>unsuccessful, and it has been closed.</p>
				</>
			);
		}

		if (status === 'Case Aborted') {
			return 'Lawyer will not pursue it.';
		}

		if (status === 'Case Resolved' || status === 'Resolved Case') {
			return (
				<>
					<p>The case has been successfully</p>
					<p>completed and is now closed.</p>
				</>
			);
		}

		return '';
	};

	const isGray = statusColor === 'gray';
	const isGreen = statusColor === 'green';
	const isRed = statusColor === 'red';
	const isCheck = statusColor === 'check';
	const isX = statusColor === 'x';

	const getLabelStatus = () => {
		if (phaseName && !HIDE_PIN_STATUSES_CASE.includes(status)) {
			return phaseName;
		}
		if (status === 'Case Aborted' || status === 'Aborted') {
			return 'Case Has Ended';
		}
		return status;
	};

	// phaseName && !HIDE_PIN_STATUSES_CASE.includes(status)
	// ? phaseName
	// : status

	return (
		<div className={clsx('flex items-center', { 'gap-2': showDot })}>
			<TooltipComponent
				className="w-fit justify-center"
				label={getText()}
				trigger={
					<div className="flex items-center justify-start">
						<Check
							size={15}
							color="#40bb71"
							className={clsx('mr-2', {
								hidden:
									status !== 'Case Resolved' &&
									status !== 'Resolved Case' &&
									!isCheck,
							})}
						/>

						<X
							size={15}
							color="#FF0043"
							className={clsx('mr-2', {
								hidden: !END_STATUSES_CASE.includes(status) && !isX,
							})}
						/>

						<List
							size={16}
							color="#8039DF"
							className={clsx({
								hidden: status !== 'View All',
							})}
						/>
						<Image
							alt="alphabetical order"
							src={Icons.alphabeticalOrder}
							width={16}
							height={16}
							className={clsx('text-primary', {
								hidden: status !== 'Alphabetical Order',
							})}
							unoptimized
						/>
						{showDot && (
							<div
								className={clsx(
									'flex items-center justify-center rounded-full w-3 h-3',
									{
										'green-gradient': isGreen || status === 'In Progress',
										'red-gradient': isRed || status === 'Action Required',
										'gray-gradient': isGray || status === 'Case Not Started',
										'yellow-gradient': status === 'On Hold',
										hidden:
											status === 'Case Resolved' ||
											status === 'Resolved Case' ||
											status === 'Alphabetical Order' ||
											status === 'View All' ||
											END_STATUSES_CASE.includes(status),
									}
								)}
							>
								<span
									className={clsx('w-2 h-2 rounded-full', {
										'bg-green-120': isGreen || status === 'In Progress',
										'bg-main-red': isRed || status === 'Action Required',
										'bg-dark-40': status === 'Archived',
										'bg-yellow-base': status === 'On Hold',
										'bg-gray-80': isGray || status === 'Case Not Started',
									})}
								/>
							</div>
						)}
					</div>
				}
			/>
			{withLabel && (
				<Typography type="semibold-14" className="capitalize">
					{getLabelStatus()}
				</Typography>
			)}
		</div>
	);
};
